import {useSearchParams} from 'react-router-dom'
import {useRef} from 'react'
import {z} from 'zod'

export const FilterItemSchema = z.object({
    id: z.string(),
    name: z.string()
})

export const FilterFormValues = z.object({
    type_of_measure: z.array(FilterItemSchema),
    general_role: z.array(FilterItemSchema),
    site: z.array(FilterItemSchema),
    counter_part: z.array(FilterItemSchema),
    section: z.array(FilterItemSchema),
    panel: z.array(FilterItemSchema),
    activeTab: z.enum(['all', 'opinion', 'judgment', 'decree', 'ordinance']),
    general_number: z.string(),
    page: z.number(),
    perPage: z.number()
})

export type FilterFormValues = z.infer<typeof FilterFormValues>
export type FilterItem = {id: string; name: string}
export type ArrayFilterFieldNames = Exclude<keyof FilterFormValues, 'activeTab' | 'page' | 'perPage' | 'general_number'>
export type FilterFieldNames = keyof FilterFormValues

export const defaultValues: FilterFormValues = {
    type_of_measure: [],
    general_role: [],
    site: [],
    counter_part: [],
    section: [],
    panel: [],
    general_number: '',
    activeTab: 'all',
    page: 1,
    perPage: 50
}

export const useFilterMeasures = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    // Stato interno per mantenere i filtri completi
    const filtersRef = useRef<FilterFormValues>({...defaultValues})

    const getFiltersFromUrl = () => {
        const filtersFromUrl = {...defaultValues}

        const pageParam = searchParams.get('page')
        const perPageParam = searchParams.get('perPage')
        if (pageParam) filtersFromUrl.page = parseInt(pageParam, 10) || defaultValues.page
        if (perPageParam) filtersFromUrl.perPage = parseInt(perPageParam, 10) || defaultValues.perPage

        const activeTabParam = searchParams.get('activeTab')
        if (
            activeTabParam === 'opinion' ||
            activeTabParam === 'judgment' ||
            activeTabParam === 'all' ||
            activeTabParam === 'decree' ||
            activeTabParam === 'ordinance'
        ) {
            filtersFromUrl.activeTab = activeTabParam
        }

        const arrayFields: ArrayFilterFieldNames[] = [
            'type_of_measure',
            'general_role',
            'site',
            'counter_part',
            'section',
            'panel'
        ]

        arrayFields.forEach(field => {
            const value = searchParams.get(field)
            if (value) {
                const ids = value.split(',')
                filtersFromUrl[field] = ids.map(id => {
                    // Provo a mantenere il name esistente se presente nello stato interno
                    const existing = filtersRef.current[field].find(item => item.id === id)
                    return existing ? existing : {id, name: ''}
                })
            }
        })

        const generalNumber = searchParams.get('general_number')
        if (generalNumber) {
            filtersFromUrl.general_number = generalNumber
        }

        return filtersFromUrl
    }

    const setFiltersInUrl = (newFilters: Partial<FilterFormValues>) => {
        const params = new URLSearchParams(searchParams)

        const isOnlyPageChange = Object.keys(newFilters).length === 1 && 'page' in newFilters
        if (!isOnlyPageChange) {
            params.set('page', '1')
        }

        Object.entries(newFilters).forEach(([key, value]) => {
            if (key === 'general_number' && typeof value === 'string') {
                value ? params.set(key, value) : params.delete(key)
            } else if (Array.isArray(value)) {
                if (value.length > 0) {
                    const ids = value.map(item => item.id).join(',')
                    params.set(key, ids)
                } else {
                    params.delete(key)
                }
            } else if (key === 'activeTab' && typeof value === 'string') {
                value === 'all' ? params.delete(key) : params.set(key, value)
            } else if (key === 'page' && typeof value === 'number') {
                params.set(key, value.toString())
            } else if (key === 'perPage' && typeof value === 'number' && value !== defaultValues.perPage) {
                params.set(key, value.toString())
            }
        })

        // 🔁 Salva i valori completi in memoria (non nel path)
        filtersRef.current = {
            ...filtersRef.current,
            ...newFilters
        }

        setSearchParams(params, {replace: true})
    }

    const resetFilters = () => {
        filtersRef.current = {...defaultValues}

        const params = new URLSearchParams()
        const currentQuery = searchParams.get('query')
        const currentPage = searchParams.get('page')
        const currentPerPage = searchParams.get('perPage')

        if (currentQuery) params.set('query', currentQuery)
        if (currentPage) params.set('page', currentPage)
        if (currentPerPage) params.set('perPage', currentPerPage)

        setSearchParams(params, {replace: true})
    }

    const filters = getFiltersFromUrl()

    const filterSelected = Object.keys(filters).filter(key => {
        if (key === 'activeTab' || key === 'page' || key === 'perPage') return false
        const value = filters[key as keyof FilterFormValues]
        if (key === 'general_number') {
            return typeof value === 'string' && value.length > 0
        }
        return Array.isArray(value) && value.length > 0
    }) as (keyof FilterFormValues)[]

    return {
        filters,
        setFilters: setFiltersInUrl,
        resetFilters,
        filterSelected,
        filterCounter: filterSelected.length
    }
}
